<template>
  <div class="flex flex-col min-h-screen">
    <Header :loaded="appointment !== null" />

    <div v-if="appointment">
      <div
        class="bg-center bg-cover bg-no-repeat"
        :style="{
          backgroundImage: 'url(' + require('../assets/header.jpg') + ')'
        }"
      >
        <div
          class="container flex flex-col items-center md:items-start justify-center relative md:pb-32 pt-8 md:pt-32 mb-16 md:mb-0"
        >
          <div class="flex justify-center md:justify-start w-full md:w-2/5">
            <div class="w-full">
              <img src="../assets/logo.svg" alt="Logo MEYLExperience" />
            </div>
          </div>

          <div
            class="w-full md:w-auto mt-32 -mb-24 md:mt-0 relative md:absolute md:top-0 md:right-0"
          >
            <Countdown :appointment="appointment">
              <a
                v-if="appointment.teamsLink"
                :href="appointment.teamsLink"
                target="_blank"
                class="transition-colors ease-out duration-200 block text-white border-2 border-orange bg-orange font-bold text-center rounded-lg py-2 px-4 leading-none hover:bg-white hover:text-orange"
              >
                {{ $t('teilnehmen') }}
              </a>
            </Countdown>
          </div>
        </div>
      </div>

      <section id="mein-termin" class="pt-16">
        <div class="container">
          <div class="text-center lg:text-left">
            <h2 class="font-bold text-blue text-2xl lg:text-4xl mb-4">
              {{ $t('terminMitMeyle') }}
            </h2>
            <p class="mb-4 lg:mb-2" v-if="this.client">
              {{
                $t('welcome', {
                  displayName: clientDisplayName
                })
              }}
            </p>
            <p class="mb-4 lg:mb-2" v-else>
              {{ $t('welcomeNoClient') }}
            </p>
          </div>
          <div class="p-5 rounded-lg bg-gray mb-8">
            <div class="flex flex-wrap -mx-2">
              <div class="px-2 w-full lg:w-1/2 mb-5 lg:mb-0">
                <div
                  class="bg-white text-center lg:text-left px-3 lg:px-8 py-5 rounded-lg border-l-15 border-blue h-full"
                >
                  <div class="mb-1">{{ weekday }}</div>
                  <div class="font-bold text-blue text-4xl mb-1 leading-none">
                    {{ date }}
                  </div>
                  <div>
                    {{ startTime }} - {{ endTime }}
                    <span v-if="$i18n.locale === 'de'">Uhr</span>
                  </div>
                  <div
                    class="mt-0 lg:mt-2 flex flex-wrap lg:flex-no-wrap justify-between -mx-2"
                  >
                    <div
                      class="px-2 mt-2 lg:mt-0 w-full lg:w-auto flex justify-center lg:justify-start"
                    >
                      <ShareLink :appointment="appointment" />
                    </div>
                    <div class="px-2 mt-2 lg:mt-0 w-full lg:w-auto">
                      <a
                        v-if="isOver && appointment.teamsLink"
                        :href="appointment.teamsLink"
                        target="_blank"
                        class="transition-colors ease-out duration-200 block w-full lg:w-auto text-white border-2 border-blue bg-blue font-bold text-center rounded-lg py-2 px-4 lg:px-16 leading-none hover:bg-white hover:text-blue"
                      >
                        {{ $t('teilnehmen') }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div class="px-2 w-full lg:w-1/2">
                <div
                  class="bg-white text-center lg:text-left px-3 lg:px-8 py-5 rounded-lg h-full"
                >
                  <h3 class="lg:text-lg text-blue font-bold mb-2">
                    {{ $t('ihrAccountManager') }}
                  </h3>
                  <div class="flex flex-wrap lg:flex-no-wrap items-center">
                    <div class="mx-auto lg:ml-0 lg:mr-4 w-20 h-20 mb-5 lg:mb-0">
                      <img
                        :src="accountManagerPortrait"
                        alt=""
                        class="rounded-full border-2 border-gray-dark"
                      />
                    </div>
                    <div class="leading-snug w-full lg:w-auto">
                      {{ appointment.accountManager.display_name }}<br />
                      MEYLE Account Manager<br />
                      <span v-if="accountManagerPhone"
                        >Tel.: {{ accountManagerPhone }} |
                        <br class="lg:hidden" />
                      </span>
                      <a
                        :href="`mailto:${appointment.accountManager.email}`"
                        class="hover:underline"
                        >{{ appointment.accountManager.email }}</a
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3
              class="text-base lg:text-lg text-center lg:text-left text-blue font-bold mb-4 lg:mb-12"
            >
              {{ $t('geplanteAgenda') }}
            </h3>
            <div class="flex -mx-4 flex-wrap mb-8">
              <div class="px-4 w-full lg:w-1/2 mb-4 lg:mb-8">
                <agenda-item
                  :number="1"
                  :title="$t('onboarding')"
                  :image="require('../assets/teaser/onboarding.jpg')"
                  :email="appointment.accountManager.email"
                  :name="appointment.accountManager.display_name"
                  :copy="$t('islandDescription.onboarding')"
                />
              </div>

              <div
                class="px-4 w-full lg:w-1/2 mb-4 lg:mb-8"
                v-for="(agenda, index) in appointment.agenda"
                :key="agenda.island.number"
              >
                <agenda-item
                  :number="index + 2"
                  :title="$t(`island.${agenda.island.number}`)"
                  :image="
                    require(`../assets/teaser/${agenda.island.number}.jpg`)
                  "
                  :email="agenda.expert.email"
                  :name="`${agenda.expert.firstName} ${agenda.expert.lastName}`"
                  :copy="$t(`islandDescription.${agenda.island.number}`)"
                />
              </div>

              <div class="px-4 w-full lg:w-1/2 mb-4 lg:mb-8">
                <agenda-item
                  :number="appointment.agenda.length + 2"
                  :title="$t('offboarding')"
                  :image="require('../assets/teaser/offboarding.jpg')"
                  :email="appointment.accountManager.email"
                  :name="appointment.accountManager.display_name"
                  :copy="$t('islandDescription.offboarding')"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="so-funktionierts" class="pt-3 lg:pt-16">
        <div class="container">
          <h2
            class="font-bold text-blue text-2xl text-center lg:text-left lg:text-4xl mb-8"
          >
            {{ $t('howTo') }}
          </h2>
          <carousel
            carousel-class="flex flex-wrap -mx-4 lg:-mx-6 mb-8"
            mobile-only
          >
            <slide
              v-for="index in 9"
              :key="index"
              class="px-4 lg:px-6 w-full lg:w-1/3 mb-8"
            >
              <howto-item
                :link="$t(`howto.${index}.link`)"
                :icon="require(`../assets/icons/${index}.svg`)"
                :title="$t(`howto.${index}.title`)"
                :copy="$t(`howto.${index}.copy`)"
              />
            </slide>
          </carousel>

          <div class="flex -mx-4 flex-wrap mt-4 lg:mt-0">
            <div class="px-4 w-full lg:w-1/3 mb-5 lg:mb-0">
              <img
                :src="require(`../assets/ueberblick_${$i18n.locale}.jpg`)"
                alt=""
              />
            </div>
            <div class="px-4 w-full lg:w-2/3">
              <h3 class="text-lg text-blue font-bold mb-5 lg:mb-6">
                {{ $t('ueberblick') }}
              </h3>
              <p>
                {{ $t('ueberblickCopy') }}
              </p>
              <div class="flex mt-4">
                <Modal>
                  <div>
                    <img
                      :src="require(`../assets/messekarte_${$i18n.locale}.jpg`)"
                      alt=""
                    />
                  </div>
                  <template #trigger="{open}">
                    <button
                      @click="open"
                      class="transition-colors ease-out duration-200 block w-full md:w-auto text-white border-2 border-blue bg-blue font-bold text-center rounded-lg py-2 px-16 leading-none hover:bg-white hover:text-blue"
                    >
                      {{ $t('ueberblickCta') }}
                    </button>
                  </template>
                </Modal>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="vorschau" class="pt-8 pb-3 lg:py-16">
        <div class="container">
          <h2
            class="font-bold text-blue text-2xl text-center lg:text-left lg:text-4xl mb-8"
          >
            {{ $t('vorschauTitle') }}
          </h2>

          <carousel>
            <slide>
              <main-video />
            </slide>
            <slide v-for="index in 3" :key="index">
              <div>
                <img
                  :src="require(`../assets/Vorschau_2180x840_0${index}.jpg`)"
                  alt=""
                  class="block w-full"
                />
              </div>
            </slide>
          </carousel>
          <div class="mt-4">
            <h3 class="text-xl font-bold mb-4 text-blue">
              {{ $t('vorschauHeadline') }}
            </h3>
            <p class="mb-4">{{ $t('vorschauCopy.1') }}</p>
            <p class="mb-4">{{ $t('vorschauCopy.2') }}</p>
            <p class="mb-4">{{ $t('vorschauCopy.3') }}</p>
          </div>
        </div>
      </section>
    </div>

    <div v-else class="flex-1">
      <Loader />
    </div>

    <Footer />
  </div>
</template>

<script>
import Vue from 'vue';
import http from '../http';
import Countdown from '../components/Countdown.vue';
import AgendaItem from '../components/AgendaItem.vue';
import HowtoItem from '../components/HowtoItem.vue';
import Carousel from '../components/Carousel.vue';
import Footer from '../components/Footer.vue';
import Header from '../components/Header.vue';
import Loader from '../components/Loader.vue';
import Slide from '../components/Slide.vue';
import Modal from '../components/Modal.vue';
import ShareLink from '../components/ShareLink.vue';
import MainVideo from '../components/MainVideo.vue';
import { format, differenceInSeconds } from 'date-fns';
import dateFnsLocales from '../utils/dateFnsLocales';
import { getPhone, getPortrait } from '../utils/getMeta';

export default Vue.extend({
  components: {
    Countdown,
    AgendaItem,
    HowtoItem,
    Carousel,
    Footer,
    Header,
    Loader,
    Slide,
    Modal,
    ShareLink,
    MainVideo
  },

  data() {
    return {
      appointment: null,
      interval: null,
      now: new Date()
    };
  },

  computed: {
    appointmentId() {
      return this.$route.params.appointment;
    },
    startDate() {
      return new Date(this.appointment.startDate);
    },
    endDate() {
      return new Date(this.appointment.endDate);
    },
    clientId() {
      return this.$route.params.client;
    },
    client() {
      return this.appointment.clients.find(
        client => client.uuid === this.clientId
      );
    },
    clientDisplayName() {
      if (!this.client) {
        return this.$i18n.t('gast');
      }

      return `${this.client.firstName} ${this.client.lastName}`;
    },
    weekday() {
      return format(this.startDate, 'EEEE', {
        locale: dateFnsLocales[this.$i18n.locale]
      });
    },
    date() {
      return format(this.startDate, 'dd.MM.yyyy', {
        locale: dateFnsLocales[this.$i18n.locale]
      });
    },
    startTime() {
      return format(this.startDate, 'HH:mm', {
        locale: dateFnsLocales[this.$i18n.locale]
      });
    },
    endTime() {
      return format(this.endDate, 'HH:mm', {
        locale: dateFnsLocales[this.$i18n.locale]
      });
    },
    accountManagerPhone() {
      return getPhone(this.appointment.accountManager.email);
    },
    accountManagerPortrait() {
      return getPortrait(this.appointment.accountManager.email);
    },
    isOver() {
      return differenceInSeconds(this.startDate, this.now) <= 0;
    }
  },

  methods: {
    async fetchAppointment() {
      const { data } = await http.get(`/api/foyer/${this.appointmentId}`);

      this.appointment = data.data;

      if (this.client) {
        this.$i18n.locale = this.client.language;
      } else {
        if (/^de/.test(navigator.language)) {
          this.$i18n.locale = 'de';
        } else {
          this.$i18n.locale = 'en';
        }
      }
    },
    updateNow() {
      this.now = new Date();
    }
  },
  created() {
    this.interval = setInterval(this.updateNow, 1000);
  },
  beforeDestroy() {
    clearInterval(this.interval);
  },
  mounted() {
    this.fetchAppointment();
  }
});
</script>

<style></style>
